// src/components/charts/base/StatCard.tsx
/**
 * StatCard Component
 * 
 * A KPI/stat card for displaying single metrics with:
 * - Primary value with customizable formatting
 * - Change indicator with trend styling
 * - Optional subtitle and period label
 * - Compact variant for grid layouts
 * - Consistent dark theme styling
 * 
 * @example
 * // Full-size stat card
 * <StatCard
 *   label="Total Market Cap"
 *   value={2500000000000}
 *   format="currency"
 *   change={5.2}
 *   changePeriod="24h"
 *   trend="up"
 * />
 * 
 * @example
 * // Compact variant
 * <StatCard
 *   label="ETH Price"
 *   value={3200}
 *   format="currency"
 *   change={-2.1}
 *   trend="down"
 *   compact
 * />
 */

import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { CHART_COLORS, CARD_ACCENTS, getSemanticColors } from '@/utils/charts/theme';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type StatValueFormat = 'currency' | 'percent' | 'ratio' | 'number' | 'multiplier' | 'compact';
export type StatTrend = 'up' | 'down' | 'neutral';

export interface StatCardProps {
  /** Stat label/title */
  label: string;
  /** Primary value (number or pre-formatted string) */
  value: number | string;
  /** Value format type */
  format: StatValueFormat;
  /** Change percentage */
  change?: number | undefined;
  /** Change period label (e.g., "24h", "7d") */
  changePeriod?: string | undefined;
  /** Trend direction */
  trend?: StatTrend | undefined;
  /** Optional subtitle */
  subtitle?: string | undefined;
  /** Compact variant for grids */
  compact?: boolean;
  /** Accent color as RGB values */
  accentColor?: string;
  /** Primary color as hex */
  primaryColor?: string;
  /** Additional className */
  className?: string;
  /** Disable animation */
  disableAnimation?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatStatValue(value: number | string, format: StatValueFormat): string {
  if (typeof value === 'string') return value;
  
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: Math.abs(value) >= 1e9 ? 'compact' : 'standard',
        maximumFractionDigits: Math.abs(value) >= 1000 ? 0 : 2,
      }).format(value);
    
    case 'percent':
      return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
    
    case 'ratio':
      return value.toFixed(2);
    
    case 'number':
      return new Intl.NumberFormat('en-US').format(value);
    
    case 'multiplier':
      return `${value.toFixed(1)}x`;
    
    case 'compact':
      return new Intl.NumberFormat('en-US', { notation: 'compact' }).format(value);
    
    default:
      return String(value);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Default Colors (from unified theme)
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_CARD_ACCENT = CARD_ACCENTS[0]!;
const SEMANTIC_COLORS = getSemanticColors('standard');

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  format,
  change,
  changePeriod,
  trend,
  subtitle,
  compact = false,
  accentColor = DEFAULT_CARD_ACCENT.accent,
  primaryColor = DEFAULT_CARD_ACCENT.primary,
  className = '',
  disableAnimation = false,
}) => {
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const trendColor = trend === 'up' 
    ? SEMANTIC_COLORS.positive 
    : trend === 'down' 
      ? SEMANTIC_COLORS.negative 
      : CHART_COLORS.text.muted;
  
  const formattedValue = formatStatValue(value, format);
  
  const hasChange = change !== undefined && change !== null;
  const changePrefix = hasChange && change >= 0 ? '+' : '';
  const changeFormatted = hasChange 
    ? `${changePrefix}${change.toFixed(2)}%` 
    : null;

  const Wrapper = disableAnimation ? 'div' : motion.div;
  const wrapperProps = disableAnimation ? {} : {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3 },
  };

  // Compact variant
  if (compact) {
    return (
      <Wrapper
        {...wrapperProps}
        className={`relative rounded-xl p-4 overflow-hidden ${className}`}
        style={{
          background: 'linear-gradient(145deg, rgba(25,25,35,0.9) 0%, rgba(35,35,45,0.7) 100%)',
          border: `1px solid rgba(${accentColor}, 0.15)`,
        }}
      >
        {/* Subtle glow */}
        <div 
          className="absolute top-0 right-0 w-20 h-20 pointer-events-none"
          style={{
            background: `radial-gradient(circle at top right, rgba(${accentColor}, 0.1) 0%, transparent 70%)`,
          }}
        />
        
        <div className="relative z-10">
          <p className="text-xs text-white/50 mb-1 truncate">{label}</p>
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-bold text-white/90">{formattedValue}</span>
            {changeFormatted && (
              <span 
                className="text-xs font-medium flex items-center gap-0.5"
                style={{ color: trendColor }}
              >
                <TrendIcon className="h-3 w-3" />
                {changeFormatted}
              </span>
            )}
          </div>
          {changePeriod && (
            <p className="text-[10px] text-white/30 mt-1">{changePeriod}</p>
          )}
        </div>
      </Wrapper>
    );
  }

  // Full-size variant
  return (
    <Wrapper
      {...wrapperProps}
      className={`relative rounded-2xl overflow-hidden my-5 ${className}`}
      style={{
        background: 'linear-gradient(145deg, rgba(20,20,30,0.9) 0%, rgba(28,28,38,0.7) 100%)',
        border: `1px solid rgba(${accentColor}, 0.2)`,
        boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
      }}
    >
      {/* Corner accent */}
      <div 
        className="absolute top-0 right-0 w-32 h-32 pointer-events-none"
        style={{
          background: `radial-gradient(circle at top right, rgba(${accentColor}, 0.08) 0%, transparent 70%)`,
        }}
      />
      
      {/* Top accent line */}
      <div 
        className="absolute top-0 left-0 right-0 h-[1px]"
        style={{
          background: `linear-gradient(90deg, transparent 10%, rgba(${accentColor}, 0.4) 50%, transparent 90%)`,
        }}
      />

      <div className="relative z-10 p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-white/50 mb-2">{label}</p>
            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-bold text-white/95">{formattedValue}</span>
              {changeFormatted && (
                <div 
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full text-sm font-medium"
                  style={{ 
                    backgroundColor: `${trendColor}15`,
                    color: trendColor,
                  }}
                >
                  <TrendIcon className="h-3.5 w-3.5" />
                  {changeFormatted}
                </div>
              )}
            </div>
            {changePeriod && (
              <p className="text-xs text-white/30 mt-2">{changePeriod}</p>
            )}
            {subtitle && (
              <p className="text-xs text-white/40 mt-2">{subtitle}</p>
            )}
          </div>
          
          <div 
            className="h-10 w-10 rounded-xl flex items-center justify-center"
            style={{ background: `rgba(${accentColor}, 0.15)` }}
          >
            <TrendIcon className="h-5 w-5" style={{ color: primaryColor }} />
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default StatCard;

